/**
 * Timesheet — Supabase service.
 *
 * Direct Supabase client queries for the `timesheet.*` schema (migration
 * 00056). Mirrors the methods on timesheetDataService that need real
 * persistence; the hardcoded employee/contract/charge-code lookups stay on
 * the mock path until the Tesseract-table mapper sub-task lands (BACKLOG.md
 * "Frontend mapper implications").
 *
 * Status mapping: the DB has draft/submitted/approved/rejected/recalled while
 * the app type uses pending/approved/rejected/escalated. submitted → pending
 * preserves the existing UI rendering; draft + recalled rows are filtered out.
 */
import { supabase } from '@/lib/supabase'
import { writeRequestDecisionNotification } from '@/services/notificationService'
import type {
  TsTimeEntry, TsTimeEntrySource, TsSubmission, ApprovalDecision,
  TsEmployee, TsContract, TsChargeCode, TsWorkflowEvent,
  ClearanceLevel, ContractType,
} from '../types/timesheet.types'
import { addDays, toIsoDate } from './timesheetMockData'
import type { RosterIdentity } from '@/lib/productionIdentity'
import { getCurrentOrganizationId } from '@/lib/orgContext'
import { getStoredApprovalMethodology } from '../lib/timesheetConfig'
import {
  routeSubmission, deriveSubmissionStatus,
  type RoutingEntry, type SegmentStatus,
} from '../lib/approvalRouting'
import { listChargeObjectApprovers } from './chargeObjectApproverService'
import { listActiveOrFutureDelegations } from './supervisorDelegationService'
import { activeDelegatorsFor } from '../lib/supervisorDelegation'

type DbTimeEntry = {
  id: string
  employee_id: string
  date: string
  charge_code_id: string
  labor_category: string | null
  hours: number
  notes: string | null
  late_justification: string | null
  is_billable?: boolean | null
  customer_id?: string | null
  source?: TsTimeEntrySource | null
  wand_segment_id?: string | null
  created_at?: string | null
  routed_approver_id?: string | null
  start_time?: string | null
  end_time?: string | null
  break_minutes?: number | null
}

type DbSubmission = {
  id: string
  employee_id: string
  week_start: string
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'recalled'
  submitted_at: string | null
  approved_at?: string | null
  rejection_reason?: string | null
  total_hours: number | null
  workflow_instance_id?: string | null
}

function rowToEntry(row: DbTimeEntry): TsTimeEntry {
  return {
    id: row.id,
    employeeId: row.employee_id,
    date: row.date,
    chargeCodeId: row.charge_code_id,
    laborCategory: row.labor_category ?? '',
    hours: Number(row.hours),
    notes: row.notes ?? null,
    lateJustification: row.late_justification,
    isBillable: row.is_billable ?? false,
    customerId: row.customer_id ?? null,
    source: row.source ?? 'manual',
    wandSegmentId: row.wand_segment_id ?? null,
    createdAt: row.created_at ?? undefined,
    routedApproverId: row.routed_approver_id ?? null,
    startTime: row.start_time ?? null,
    endTime: row.end_time ?? null,
    breakMinutes: row.break_minutes ?? null,
  }
}

function dbStatusToApp(s: DbSubmission['status']): ApprovalDecision | null {
  switch (s) {
    case 'submitted': return 'pending'
    case 'approved': return 'approved'
    case 'rejected': return 'rejected'
    case 'draft':
    case 'recalled':
      return null  // filtered out
  }
}

function rowToSubmission(row: DbSubmission): TsSubmission | null {
  const status = dbStatusToApp(row.status)
  if (!status) return null
  return {
    id: row.id,
    employeeId: row.employee_id,
    weekStart: row.week_start,
    totalHours: Number(row.total_hours ?? 0),
    status,
    submittedAt: row.submitted_at ?? new Date().toISOString(),
    // approved_at is the decision stamp for BOTH outcomes (rejections write it
    // too); it lets My Requests bubble a fresh decision to the top instead of
    // burying it at its submission date (Connor 2026-07-26).
    decidedAt: row.approved_at ?? null,
    rejectionReason: row.rejection_reason ?? null,
    anomalyCount: 0,  // populated by hooks once anomaly detection is wired
  }
}

// ─── Reads ────────────────────────────────────────────────────────────────────

const ENTRY_COLS_BASE = 'id, employee_id, date, charge_code_id, labor_category, hours, notes, late_justification, is_billable, customer_id, source, wand_segment_id, created_at'
// Extended columns added by later migrations: routed_approver_id (approval
// routing) and start_time / end_time / break_minutes (hourly start/stop entry,
// migration 00378). Select them when present, but tolerate their absence so a
// pre-migration deploy keeps reading entries (this is the hot path for the whole
// My Timesheet grid — it must not hard-fail).
const ENTRY_COLS = `${ENTRY_COLS_BASE}, routed_approver_id, start_time, end_time, break_minutes`

export async function getEntriesForWeek(employeeId: string, weekStart: string): Promise<TsTimeEntry[]> {
  const start = weekStart
  const end = toIsoDate(addDays(new Date(weekStart), 7))
  const run = (cols: string) =>
    supabase
      .from('timesheet_time_entries')
      .select(cols)
      .eq('employee_id', employeeId)
      .gte('date', start)
      .lt('date', end)
      .order('date')
  let { data, error } = await run(ENTRY_COLS)
  if (error) {
    // Pre-00380: routed_approver_id absent — retry without it.
    ;({ data, error } = await run(ENTRY_COLS_BASE))
    if (error) throw error
  }
  return (data as unknown as DbTimeEntry[]).map(rowToEntry)
}

/**
 * ALL workers' entries in a date range, ONE query — the workforce-wide unit of
 * access audit scans need. The Late-Entry Audit previously fetched
 * per-worker-per-week (employees × weeks sequential round-trips: ~160 requests
 * for a 4-week window — the page took 30s+ to settle). Same column-fallback
 * behavior as getEntriesForWeek. Both bounds inclusive.
 */
export async function getEntriesInRange(startIso: string, endIso: string): Promise<TsTimeEntry[]> {
  const run = (cols: string) =>
    supabase
      .from('timesheet_time_entries')
      .select(cols)
      .gte('date', startIso)
      .lte('date', endIso)
      .order('date')
      .limit(20000)
  let { data, error } = await run(ENTRY_COLS)
  if (error) {
    ;({ data, error } = await run(ENTRY_COLS_BASE))
    if (error) throw error
  }
  return (data as unknown as DbTimeEntry[]).map(rowToEntry)
}

export async function getSubmissions(employeeId?: string): Promise<TsSubmission[]> {
  let q = supabase
    .from('timesheet_submissions')
    .select('id, employee_id, week_start, status, submitted_at, approved_at, rejection_reason, total_hours')
    .order('week_start', { ascending: false })
  if (employeeId) q = q.eq('employee_id', employeeId)
  const { data, error } = await q
  if (error) throw error
  return (data as DbSubmission[]).map(rowToSubmission).filter((s): s is TsSubmission => s !== null)
}

/**
 * Fetch one submission by id. Used by the Floor Check detail page to
 * load the auditor view from a deep-link.
 */
export async function getSubmissionById(id: string): Promise<TsSubmission | null> {
  const { data, error } = await supabase
    .from('timesheet_submissions')
    .select('id, employee_id, week_start, status, submitted_at, approved_at, rejection_reason, total_hours, workflow_instance_id')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  return rowToSubmission(data as DbSubmission)
}

/**
 * Approval-workflow history for a week's submission — the events behind the
 * inline "Submission status" tracker on My Timesheet (submitted → manager
 * review → approved/returned). Reads `workflow_instances.history` (the durable
 * audit chain spawned by submitWeek) and resolves each actor_id to a display
 * name via the roster. Best-effort + non-fatal: returns [] when the week has no
 * submission, no attached workflow (predates 00058), or the read is denied —
 * the tracker then falls back to status + submittedAt alone.
 */
export async function getSubmissionHistory(
  employeeId: string, weekStart: string,
): Promise<TsWorkflowEvent[]> {
  const { data: subRow } = await supabase
    .from('timesheet_submissions')
    .select('workflow_instance_id')
    .eq('employee_id', employeeId)
    .eq('week_start', weekStart)
    .maybeSingle()
  const wfId = (subRow as { workflow_instance_id: string | null } | null)?.workflow_instance_id
  if (!wfId) return []

  const { data: instRow } = await supabase
    .from('workflow_instances')
    .select('history')
    .eq('id', wfId)
    .maybeSingle()
  const raw = ((instRow as { history: unknown[] | null } | null)?.history) ?? []
  if (!Array.isArray(raw) || raw.length === 0) return []

  // Resolve actor names from the roster (id OR pernr, matching getEmployee).
  const roster = await fetchEmployees().catch(() => [] as TsEmployee[])
  const nameForActor = (actorId: string | null): string | null => {
    if (!actorId) return null
    return roster.find((e) => e.id === actorId)?.name
      ?? roster.find((e) => e.pernr === actorId)?.name
      ?? null
  }

  return (raw as Array<{ event?: string; at?: string; actor_id?: string | null }>)
    .filter((h) => h && typeof h.at === 'string')
    .map((h) => ({
      event: h.event ?? 'event',
      at: h.at as string,
      actorId: h.actor_id ?? null,
      actorName: nameForActor(h.actor_id ?? null),
    }))
}

/**
 * Audit-trail rows for a set of entries. Sorted oldest-first so the
 * UI renders a chronological timeline.
 */
export interface TsEntryHistoryRow {
  id: string
  entryId: string
  operation: 'insert' | 'update' | 'delete'
  changedBy: string | null
  changedAt: string
  changeReason: string | null
  beforeData: unknown
  afterData: unknown
}

export async function getHistoryForEntries(entryIds: ReadonlyArray<string>): Promise<TsEntryHistoryRow[]> {
  if (entryIds.length === 0) return []
  const { data, error } = await supabase
    .from('timesheet_time_entries_history')
    .select('id, entry_id, operation, changed_by, changed_at, change_reason, before_data, after_data')
    .in('entry_id', entryIds as string[])
    .order('changed_at', { ascending: true })
  if (error) throw error
  return (data as Array<{
    id: string; entry_id: string; operation: 'insert' | 'update' | 'delete'
    changed_by: string | null; changed_at: string; change_reason: string | null
    before_data: unknown; after_data: unknown
  }>).map((r) => ({
    id: r.id, entryId: r.entry_id, operation: r.operation,
    changedBy: r.changed_by, changedAt: r.changed_at,
    changeReason: r.change_reason,
    beforeData: r.before_data, afterData: r.after_data,
  }))
}

/**
 * Resolve a supervisor's direct-report ids from the LIVE roster
 * (`users.manager_id`, surfaced as `supervisorId` by fetchEmployees) — not the
 * bundled mock list, which omits the per-company casts (so a ConstructCo/Boeing
 * manager got an empty queue) and uses ids that don't match supabase
 * submissions. The supervisor may arrive as a row id or a pernr; report ids
 * cover both id + pernr so they match `submission.employee_id` regardless of
 * which persona id was active at submit time.
 */
async function resolveDirectReportIds(supervisorId: string): Promise<string[]> {
  const roster = await fetchEmployees()
  const supervisor = roster.find((e) => e.id === supervisorId || e.pernr === supervisorId)
  const supId = supervisor?.id ?? supervisorId
  const reports = roster.filter((e) => e.supervisorId === supId)
  return [...new Set(reports.flatMap((e) => [e.id, e.pernr].filter((x): x is string => !!x)))]
}

/**
 * Time Approval Routing (Part 3) — the user ids whose approval authority is
 * actively delegated to `approverId` right now. Lets a delegate see (and act on)
 * the delegator's queue. Empty + non-fatal when no delegations table / rows.
 */
async function activeDelegatorIdsForApprover(approverId: string): Promise<string[]> {
  try {
    const all = await listActiveOrFutureDelegations()
    return activeDelegatorsFor(approverId, new Date().toISOString(), all)
  } catch (e) { console.warn('[timesheet] activeDelegatorIdsForApprover', e); return [] }
}

export async function getPendingApprovals(supervisorId: string): Promise<TsSubmission[]> {
  // Include the direct reports of anyone who has actively delegated their
  // approval authority to this supervisor, so a delegate sees the queue.
  const delegators = await activeDelegatorIdsForApprover(supervisorId)
  const reportIdSets = await Promise.all([supervisorId, ...delegators].map((id) => resolveDirectReportIds(id)))
  const reportIds = [...new Set(reportIdSets.flat())]
  if (reportIds.length === 0) return []
  const { data, error } = await supabase
    .from('timesheet_submissions')
    .select('id, employee_id, week_start, status, submitted_at, approved_at, rejection_reason, total_hours')
    .in('employee_id', reportIds)
    .eq('status', 'submitted')
    .order('submitted_at', { ascending: false })
  if (error) throw error
  return (data as DbSubmission[]).map(rowToSubmission).filter((s): s is TsSubmission => s !== null)
}

// ─── Writes ───────────────────────────────────────────────────────────────────

export async function upsertEntry(entry: TsTimeEntry): Promise<void> {
  const base = {
    id: entry.id,
    employee_id: entry.employeeId,
    date: entry.date,
    charge_code_id: entry.chargeCodeId,
    labor_category: entry.laborCategory || null,
    hours: entry.hours,
    notes: entry.notes ?? null,
    late_justification: entry.lateJustification ?? null,
    is_billable: entry.isBillable ?? false,
    customer_id: entry.customerId ?? null,
    source: entry.source ?? 'manual',
    wand_segment_id: entry.wandSegmentId ?? null,
  }
  // Persist the hourly start/stop fields (migration 00378). Hours stays the
  // derived source of truth; these record HOW an hourly worker arrived at it.
  const row = {
    ...base,
    start_time: entry.startTime ?? null,
    end_time: entry.endTime ?? null,
    break_minutes: entry.breakMinutes ?? null,
  }
  let { error } = await supabase.from('timesheet_time_entries').upsert(row)
  if (error) {
    // Pre-00378 deploy: start/stop columns absent — retry without them so the
    // hours-only save still persists rather than failing the whole entry.
    ;({ error } = await supabase.from('timesheet_time_entries').upsert(base))
  }
  if (error) throw error
}

export async function deleteEntry(entryId: string): Promise<void> {
  const { error } = await supabase
    .from('timesheet_time_entries')
    .delete()
    .eq('id', entryId)
  if (error) throw error
}

// ─── Cost transfers ─────────────────────────────────────────────────────────
// Retroactive hour reassignment between charge codes, an audit-mandated formal
// approval flow. Schema: public.timesheet_cost_transfers (migration 00059).

export interface TsCostTransfer {
  id: string
  sourceEntryId: string
  requestedBy: string
  approverId: string
  fromChargeCodeId: string
  toChargeCodeId: string
  hours: number
  justification: string
  requiresLateAck: boolean
  lateAckAt: string | null
  requestedAt: string
  decision: 'pending' | 'approved' | 'rejected'
  decidedAt: string | null
  comment: string | null
}

type DbCostTransfer = {
  id: string
  source_entry_id: string
  requested_by: string
  approver_id: string
  from_charge_code_id: string
  to_charge_code_id: string
  hours: number | string
  justification: string
  requires_late_ack: boolean
  late_ack_at: string | null
  requested_at: string
  decision: 'pending' | 'approved' | 'rejected'
  decided_at: string | null
  comment: string | null
}

function rowToCostTransfer(row: DbCostTransfer): TsCostTransfer {
  return {
    id: row.id,
    sourceEntryId: row.source_entry_id,
    requestedBy: row.requested_by,
    approverId: row.approver_id,
    fromChargeCodeId: row.from_charge_code_id,
    toChargeCodeId: row.to_charge_code_id,
    hours: typeof row.hours === 'string' ? Number.parseFloat(row.hours) : row.hours,
    justification: row.justification,
    requiresLateAck: row.requires_late_ack,
    lateAckAt: row.late_ack_at,
    requestedAt: row.requested_at,
    decision: row.decision,
    decidedAt: row.decided_at,
    comment: row.comment,
  }
}

export interface CreateCostTransferInput {
  sourceEntryId: string
  requestedBy: string
  approverId: string
  fromChargeCodeId: string
  toChargeCodeId: string
  hours: number
  justification: string
  requiresLateAck: boolean  // computed at the call site by lib/costTransfer.isLateTransfer
}

export async function createCostTransfer(input: CreateCostTransferInput): Promise<TsCostTransfer> {
  const row = {
    source_entry_id: input.sourceEntryId,
    requested_by: input.requestedBy,
    approver_id: input.approverId,
    from_charge_code_id: input.fromChargeCodeId,
    to_charge_code_id: input.toChargeCodeId,
    hours: input.hours,
    justification: input.justification,
    requires_late_ack: input.requiresLateAck,
    late_ack_at: input.requiresLateAck ? new Date().toISOString() : null,
  }
  const { data, error } = await supabase
    .from('timesheet_cost_transfers')
    .insert(row)
    .select('*')
    .single()
  if (error) throw error
  return rowToCostTransfer(data as DbCostTransfer)
}

/** All transfers a worker has REQUESTED (any decision) — the worker-side
 *  "what am I waiting on / what was decided" view (MyRequestsCard). */
export async function listCostTransfersForRequester(requesterId: string): Promise<TsCostTransfer[]> {
  const { data, error } = await supabase
    .from('timesheet_cost_transfers')
    .select('*')
    .eq('requested_by', requesterId)
    .order('requested_at', { ascending: false })
    .limit(50)
  if (error) throw error
  return (data as DbCostTransfer[]).map(rowToCostTransfer)
}

export async function getPendingCostTransfersForApprover(approverId: string): Promise<TsCostTransfer[]> {
  const { data, error } = await supabase
    .from('timesheet_cost_transfers')
    .select('*')
    .eq('approver_id', approverId)
    .eq('decision', 'pending')
    .order('requested_at', { ascending: false })
  if (error) throw error
  return (data as DbCostTransfer[]).map(rowToCostTransfer)
}

export async function approveCostTransfer(id: string, comment?: string): Promise<void> {
  const { error } = await supabase
    .from('timesheet_cost_transfers')
    .update({ decision: 'approved', decided_at: new Date().toISOString(), comment: comment ?? null })
    .eq('id', id)
  if (error) throw error
  await notifyCostTransferDecision(id, 'approved', comment ?? null)
}

export async function rejectCostTransfer(id: string, comment: string): Promise<void> {
  const { error } = await supabase
    .from('timesheet_cost_transfers')
    .update({ decision: 'rejected', decided_at: new Date().toISOString(), comment })
    .eq('id', id)
  if (error) throw error
  await notifyCostTransferDecision(id, 'rejected', comment)
}

// Bell notice to the requesting worker — the decide fns only carry the row id,
// so resolve requested_by with one select. Never throws.
async function notifyCostTransferDecision(
  id: string,
  decision: 'approved' | 'rejected',
  comment: string | null,
): Promise<void> {
  try {
    const { data } = await supabase
      .from('timesheet_cost_transfers')
      .select('requested_by')
      .eq('id', id)
      .single()
    const requester = (data as { requested_by: string | null } | null)?.requested_by
    if (!requester) return
    await writeRequestDecisionNotification({
      workerId: requester,
      kind: 'cost_transfer',
      decision,
      title: decision === 'approved' ? 'Cost transfer approved' : 'Cost transfer rejected',
      summary: comment ? `Approver note: ${comment}` : null,
      entityId: id,
    })
  } catch (err) {
    console.warn('[timesheet] cost-transfer decision notice skipped:', err)
  }
}

// ─── Delegations ────────────────────────────────────────────────────────────
// Approval-authority handoffs. Tier-1 surface only — list active inbound +
// outbound for an employee. Create/revoke land when the Settings UI grows
// the form; for now SettingsPage shows the list read-only.

export interface TsDelegation {
  id: string
  delegatorId: string
  delegateId: string
  contractIds: string[] | null
  scopeStart: string
  scopeEnd: string
  reason: string
  createdAt: string
  revokedAt: string | null
}

type DbDelegation = {
  id: string
  delegator_id: string
  delegate_id: string
  contract_ids: string[] | null
  scope_start: string
  scope_end: string
  reason: string
  created_at: string
  revoked_at: string | null
}

function rowToDelegation(row: DbDelegation): TsDelegation {
  return {
    id: row.id,
    delegatorId: row.delegator_id,
    delegateId: row.delegate_id,
    contractIds: row.contract_ids,
    scopeStart: row.scope_start,
    scopeEnd: row.scope_end,
    reason: row.reason,
    createdAt: row.created_at,
    revokedAt: row.revoked_at,
  }
}

export async function getDelegationsInvolving(employeeId: string): Promise<TsDelegation[]> {
  const { data, error } = await supabase
    .from('timesheet_delegations')
    .select('id, delegator_id, delegate_id, contract_ids, scope_start, scope_end, reason, created_at, revoked_at')
    .or(`delegator_id.eq.${employeeId},delegate_id.eq.${employeeId}`)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data as DbDelegation[]).map(rowToDelegation)
}

// ─── SAP CATS bridge (mock provider — see lib/sapCats) ─────────────────────

import { getSapCatsProvider } from '../lib/sapCats'

async function postToCats(submission: TsSubmission, employeeId: string): Promise<void> {
  // Pick the first contract used in this submission's week to anchor the
  // CATS record. Real BAPI shipment writes per (submission × charge code).
  try {
    const entries = await getEntriesForWeek(employeeId, submission.weekStart)
    const firstCode = entries.find((e) => e.hours > 0)
    if (!firstCode) return
    // Resolve contract via the runtime cache (Tesseract or seed).
    const codeRecord = await supabase
      .from('charge_lines')
      .select('id, work_authorization_id')
      .eq('id', firstCode.chargeCodeId)
      .maybeSingle()
    if (!codeRecord.data) return
    const wa = await supabase
      .from('work_authorizations')
      .select('id, project_id')
      .eq('id', (codeRecord.data as { work_authorization_id: string }).work_authorization_id)
      .maybeSingle()
    if (!wa.data) return
    const to = await supabase
      .from('task_orders')
      .select('master_contract_id')
      .eq('project_id', (wa.data as { project_id: string }).project_id)
      .maybeSingle()
    const masterId = (to.data as { master_contract_id: string } | null)?.master_contract_id
    if (!masterId) return
    const contractRow = await supabase
      .from('master_contracts')
      .select('id, number, title, contractor_name, contract_type, total_value, obligated_amount, start_date, end_date')
      .eq('id', masterId)
      .maybeSingle()
    if (!contractRow.data) return
    const c = contractRow.data as DbMasterContract
    const tsContract: TsContract = {
      id: c.id,
      code: c.number ?? c.id,
      name: c.title,
      agency: c.contractor_name ?? 'Unknown',
      type: coerceContractType(c.contract_type),
      fundedValue: Number(c.total_value ?? 0),
      burnedValue: Number(c.obligated_amount ?? 0),
      ceiling: Number(c.total_value ?? 0),
      periodStart: c.start_date,
      periodEnd: c.end_date,
      casCovered: false,
      primeOrSub: 'prime',
    }
    await getSapCatsProvider().postSubmission(submission, tsContract)
  } catch (err) {
    // CATS bridge is a side channel — never fails the user-facing submit.
    console.warn('[timesheet] postToCats failed', err)
  }
}

// ─── Workflow side-channel (Tesseract approval engine) ──────────────────────
// Per BACKLOG: timesheet submissions advance through a workflow_definition
// (wf-timesheet-approval, seeded in 00058). submissions.status stays the
// user-facing state; workflow_instances + workflow_audit_log carry the
// durable audit chain. Both stay in sync via the helpers below.

const TIMESHEET_WORKFLOW_ID = 'wf-timesheet-approval'

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

async function spawnApprovalWorkflow(
  submissionId: string, employeeId: string, supervisorId: string | null, weekStart: string,
): Promise<string | null> {
  const instanceId = newId('wfi')
  const now = new Date().toISOString()
  const context = { submission_id: submissionId, employee_id: employeeId, supervisor_id: supervisorId, week_start: weekStart }
  const history = [{ event: 'submitted', step_id: 'submitted', at: now, actor_id: employeeId }]

  const { error: instErr } = await supabase
    .from('workflow_instances')
    .insert({
      id: instanceId,
      workflow_id: TIMESHEET_WORKFLOW_ID,
      workflow_name: 'Timesheet Approval',
      status: 'Active',
      current_step_id: 'manager_review',
      context,
      start_time: now,
      history,
    })
  if (instErr) {
    // Non-fatal — workflow tracking is a side-channel; submission persists either way.
    console.warn('[timesheet] workflow_instance insert failed', instErr)
    return null
  }

  await writeAuditLog(instanceId, 'submitted', employeeId, { submission_id: submissionId, week_start: weekStart })
  return instanceId
}

async function writeAuditLog(
  workflowInstanceId: string, eventType: string, actorId: string | null, payload: Record<string, unknown>,
): Promise<void> {
  const data = {
    workflow_instance_id: workflowInstanceId,
    workflow_id: TIMESHEET_WORKFLOW_ID,
    event_type: eventType,
    actor_id: actorId,
    timestamp: new Date().toISOString(),
    payload,
  }
  const { error } = await supabase.from('workflow_audit_log').insert({ data })
  if (error) console.warn('[timesheet] workflow_audit_log insert failed', error)
}

async function advanceWorkflow(
  submissionId: string, decision: 'approved' | 'rejected', actorId: string | null,
): Promise<void> {
  // Look up the workflow_instance via the submission's workflow_instance_id.
  const { data: subRow } = await supabase
    .from('timesheet_submissions')
    .select('workflow_instance_id')
    .eq('id', submissionId)
    .single()
  const wfId = (subRow as { workflow_instance_id: string | null } | null)?.workflow_instance_id
  if (!wfId) return  // No workflow attached (e.g. submission predates 00058) — skip silently.

  const now = new Date().toISOString()
  const newStepId = decision  // 'approved' | 'rejected'
  const newStatus = decision === 'approved' ? 'Completed' : 'Cancelled'

  // Append to the instance's history jsonb. Read-modify-write — Supabase doesn't
  // expose a direct jsonb_array_append helper via PostgREST without an RPC.
  const { data: instRow } = await supabase
    .from('workflow_instances')
    .select('history')
    .eq('id', wfId)
    .single()
  const currentHistory = ((instRow as { history: unknown[] | null } | null)?.history) ?? []
  const nextHistory = [...currentHistory, { event: decision, step_id: newStepId, at: now, actor_id: actorId }]

  const { error: updErr } = await supabase
    .from('workflow_instances')
    .update({ status: newStatus, current_step_id: newStepId, end_time: now, history: nextHistory })
    .eq('id', wfId)
  if (updErr) console.warn('[timesheet] workflow_instance advance failed', updErr)

  await writeAuditLog(wfId, decision, actorId, { submission_id: submissionId })
}

// Bell notice to the submitting worker on approve/reject — the decide fns only
// carry the submission id, so resolve employee_id + week_start with one select.
// Never throws (the helper swallows insert errors; a missing row just skips).
async function notifySubmissionDecision(
  submissionId: string,
  decision: 'approved' | 'rejected',
  actorId: string | null,
  reasonCode: string | null,
): Promise<void> {
  try {
    const { data } = await supabase
      .from('timesheet_submissions')
      .select('employee_id, week_start')
      .eq('id', submissionId)
      .single()
    const row = data as { employee_id: string | null; week_start: string | null } | null
    if (!row?.employee_id) return
    await writeRequestDecisionNotification({
      workerId: row.employee_id,
      kind: 'timesheet',
      decision,
      title:
        decision === 'approved'
          ? `Timesheet approved — week of ${row.week_start ?? '?'}`
          : `Timesheet returned — week of ${row.week_start ?? '?'}`,
      summary:
        decision === 'approved'
          ? (actorId ? `Approved by ${actorId}.` : null)
          : `Reopened for edits${reasonCode ? ` — reason: ${reasonCode}` : ''}. Revise and resubmit.`,
      entityId: submissionId,
      decidedBy: actorId,
      contextData: { weekStart: row.week_start },
    })
  } catch (err) {
    console.warn('[timesheet] submission decision notice skipped:', err)
  }
}

// Submission-level approve/reject effects, factored so the charge-object
// segment recompute (deriveSubmissionStatus) can drive the SAME transition,
// workflow audit, and CATS bridge as the manager-mode actions.
async function applyApprovedEffects(submissionId: string, actorId: string | null): Promise<void> {
  const { error } = await supabase
    .from('timesheet_submissions')
    .update({ status: 'approved', approved_at: new Date().toISOString(), approved_by: actorId })
    .eq('id', submissionId)
  if (error) throw error
  await notifySubmissionDecision(submissionId, 'approved', actorId, null)
  await advanceWorkflow(submissionId, 'approved', actorId)
  // CATS bridge — walk the status forward 20 → 30 → 60. Slim flow collapses
  // both transitions into the single approve action; multi-tier workflows
  // would walk one step at a time per tier.
  try {
    await getSapCatsProvider().advance(submissionId, `Approved by ${actorId ?? 'system'}`)
    await getSapCatsProvider().advance(submissionId, 'Cost transferred to CO/FI')
  } catch (err) { console.warn('[timesheet] CATS advance failed', err) }
}

async function applyRejectedEffects(submissionId: string, actorId: string | null, reasonCode: string | null): Promise<void> {
  // approved_at doubles as the decision stamp for BOTH outcomes — without it a
  // rejection sorted at its submission date in My Requests and never "came up".
  const update: Record<string, unknown> = { status: 'rejected', approved_at: new Date().toISOString(), approved_by: actorId }
  // Persist the reject reason code (previously collected in the UI but dropped).
  if (reasonCode) update.rejection_reason_code = reasonCode
  const { error } = await supabase
    .from('timesheet_submissions')
    .update(update)
    .eq('id', submissionId)
  if (error) throw error
  await notifySubmissionDecision(submissionId, 'rejected', actorId, reasonCode)
  await advanceWorkflow(submissionId, 'rejected', actorId)
  try {
    await getSapCatsProvider().reject(submissionId, `Rejected by ${actorId ?? 'system'}`)
  } catch (err) { console.warn('[timesheet] CATS reject failed', err) }
}

export async function approveSubmission(submissionId: string, actorId: string | null = null): Promise<void> {
  await applyApprovedEffects(submissionId, actorId)
}

export async function rejectSubmission(
  submissionId: string, actorId: string | null = null, reasonCode: string | null = null,
): Promise<void> {
  await applyRejectedEffects(submissionId, actorId, reasonCode)
}

// ─── Reference data (Tesseract reuse) ────────────────────────────────────────

const VALID_CLEARANCES: ClearanceLevel[] = ['None', 'Secret', 'TS', 'TS/SCI']
const VALID_CONTRACT_TYPES: ContractType[] = ['CPFF', 'FFP', 'T&M', 'CPIF', 'IDIQ', 'Indirect']

function coerceClearance(c: string | null): ClearanceLevel {
  if (!c) return 'None'
  return (VALID_CLEARANCES as string[]).includes(c) ? (c as ClearanceLevel) : 'None'
}

function coerceContractType(t: string | null): ContractType {
  if (!t) return 'FFP'
  return (VALID_CONTRACT_TYPES as string[]).includes(t) ? (t as ContractType) : 'FFP'
}

function deriveInitials(name: string, fallback: string | null): string {
  if (fallback) return fallback
  return name
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

type DbUser = {
  id: string
  name: string
  email: string
  title: string | null
  department: string | null
  initials: string | null
  system_role: string | null
  clearance: string | null
  cost_center_code: string | null
  manager_id: string | null
  exempt: boolean | null
  standard_hourly_rate: number | string | null
  weekly_salary: number | string | null
  tta_method: string | null
  authorized_labor_cats: string[] | null
  default_labor_category: string | null
  authorized_ot_codes: string[] | null
  rolled_off_contract_ids: string[] | null
  pe_status: boolean | null
  certifications: unknown
  citizenship: string | null
  grace_period_hours: number | null
  wrap_rate: number | string | null
  pernr: string | null
  // TS-MyTS — Streams A/B/G/H columns (migration 00287).
  pay_class: string | null
  schedule_type: string | null
  week_start_day: string | null
  work_category: string | null
  wand_worker_id: string | null
  custom_daily_targets: unknown
  auto_fill_enabled: boolean | null
  auto_fill_default_charge_code_id: string | null
  auto_fill_mode: string | null
  auto_fill_last_run_at: string | null
  approved_leaves: unknown
  // Stream F — subcontractor persona linkage (migration 00305)
  sub_vendor_id: string | null
  sub_employee_id: string | null
  is_sub_lead: boolean | null
  // Canonical company key (decision #5) — set by the per-company role-cast
  // migrations (00364/00365/00366). Non-null only for cast personas.
  primary_organization_id: string | null
}

type DbMasterContract = {
  id: string
  number: string | null
  title: string
  contractor_name: string | null
  contract_type: string | null
  total_value: number | null
  obligated_amount: number | null
  start_date: string | null
  end_date: string | null
  has_controlled_unclassified_info: boolean | null
}

type DbChargeLine = {
  id: string
  work_authorization_id: string
  code: string
  name: string
  resource_category_filter: string | null
  is_active: boolean
  requires_pe: boolean | null
  required_certifications: string[] | null
  export_control: string | null
  cui_marking: string | null
  // Composable dimensions (#8, migration 00309) — all nullable.
  clin_id: string | null
  cost_objective_id: string | null
  acrn: string | null
  color_of_money: string | null
  pop_start: string | null
  pop_end: string | null
  work_site: string | null
  cost_center: string | null
  gl_account: string | null
  capital_or_expense: string | null
  billable: boolean | null
  rate_card_id: string | null
  // "Choose Charge Line" axes (migration 00357) — optional: absent when the
  // migration hasn't been applied and selectChargeLines fell back to base cols.
  organization_id?: string | null
  discipline_id?: string | null
  time_type_id?: string | null
}

type DbWorkAuth = {
  id: string
  project_id: string
  wbs_element_code: string
  authorized_amount: number
  consumed_amount: number
}

type DbTaskOrder = {
  id: string
  master_contract_id: string | null
  project_id: string | null
}

function coerceTtaMethod(s: string | null): 'standard' | 'effective' | 'variance' | undefined {
  if (s === 'standard' || s === 'effective' || s === 'variance') return s
  return undefined
}

function coerceCitizenship(s: string | null): 'US' | 'USPR' | 'OTHER' | null {
  if (s === 'US' || s === 'USPR' || s === 'OTHER') return s
  return null
}

function coerceCertifications(raw: unknown): { code: string; expiresAt?: string }[] | undefined {
  if (!Array.isArray(raw)) return undefined
  return raw
    .filter((x): x is { code: string; expiresAt?: string } =>
      x != null && typeof x === 'object' && typeof (x as { code?: unknown }).code === 'string',
    )
    .map((c) => ({ code: c.code, expiresAt: c.expiresAt }))
}

/**
 * Resolve a single `users` roster row to its identity slice, by email — the
 * production-edition "tie login in with the DB" lookup (the App bootstrap calls
 * this after Supabase auth establishes a session). Returns null when no row matches
 * (e.g. an account not yet provisioned into the roster), so the caller can fall back
 * to auth metadata. See lib/productionIdentity.ts.
 */
/**
 * Score a candidate roster row so the BEST one wins when an email is not unique.
 * Higher is better. An exact auth-uid match with the live session is decisive; a
 * provisioned (auth-linked) row beats an orphan; an admin row beats a plain one.
 */
function rankRosterRow(r: Record<string, unknown>, preferAuthUid?: string | null): number {
  let score = 0
  const authUid = (r.auth_uid as string) ?? null
  if (preferAuthUid && authUid && authUid === preferAuthUid) score += 1000
  if (authUid) score += 100
  const role = String(r.system_role ?? '')
  if (role === 'GlobalAdmin' || role === 'OrgAdmin') score += 10
  return score
}

export async function fetchRosterIdentityByEmail(
  email: string,
  preferAuthUid?: string | null,
): Promise<RosterIdentity | null> {
  // NOT .maybeSingle(): a roster can end up with more than one row for an email
  // (e.g. someone re-added a person via Personnel Studio's Add User, colliding
  // with the seeded account). .maybeSingle() THROWS on that, which sends the
  // caller down the metadata fallback and silently strips the user's real role
  // + org pin — a GlobalAdmin then binds as a no-org PortfolioManager and sees
  // zero projects. Fetch all matches and pick the best deterministically.
  const { data, error } = await supabase
    .from('users')
    .select('id, name, email, initials, title, system_role, department, cost_center_code, clearance, manager_id, primary_organization_id, auth_uid')
    .eq('email', email)
  if (error) throw error
  const rows = (data ?? []) as Array<Record<string, unknown>>
  if (rows.length === 0) return null
  const r = rows.length === 1
    ? rows[0]
    : rows.slice().sort((a, b) => rankRosterRow(b, preferAuthUid) - rankRosterRow(a, preferAuthUid))[0]
  // Indexing is only `| undefined` to the type checker (rows is non-empty by the
  // guard above), but narrow it explicitly rather than asserting.
  if (!r) return null
  return {
    id: String(r.id),
    name: String(r.name ?? ''),
    email: (r.email as string) ?? null,
    initials: (r.initials as string) ?? null,
    title: (r.title as string) ?? null,
    systemRole: (r.system_role as string) ?? null,
    department: (r.department as string) ?? null,
    costCenterCode: (r.cost_center_code as string) ?? null,
    clearance: (r.clearance as string) ?? null,
    managerId: (r.manager_id as string) ?? null,
    primaryOrganizationId: (r.primary_organization_id as string) ?? null,
  }
}

export async function fetchEmployees(): Promise<TsEmployee[]> {
  const { data, error } = await supabase
    .from('users')
    .select('id, name, email, title, department, initials, system_role, clearance, cost_center_code, manager_id, exempt, standard_hourly_rate, weekly_salary, tta_method, authorized_labor_cats, default_labor_category, authorized_ot_codes, rolled_off_contract_ids, pe_status, certifications, citizenship, grace_period_hours, wrap_rate, pernr, pay_class, schedule_type, week_start_day, work_category, wand_worker_id, custom_daily_targets, auto_fill_enabled, auto_fill_default_charge_code_id, auto_fill_mode, auto_fill_last_run_at, sub_vendor_id, sub_employee_id, is_sub_lead, primary_organization_id')
    .order('name')
  if (error) throw error
  return (data as DbUser[]).map((row) => ({
    id: row.id,
    pernr: row.pernr ?? null,
    name: row.name,
    initials: deriveInitials(row.name, row.initials),
    role: row.title ?? row.system_role ?? 'Staff',
    clearance: coerceClearance(row.clearance),
    exempt: row.exempt ?? true,
    authorizedLaborCats: row.authorized_labor_cats ?? [],
    defaultLaborCategory: row.default_labor_category ?? undefined,
    standardHourlyRate: row.standard_hourly_rate != null ? Number(row.standard_hourly_rate) : undefined,
    weeklySalary: row.weekly_salary != null ? Number(row.weekly_salary) : undefined,
    ttaMethod: coerceTtaMethod(row.tta_method),
    supervisorId: row.manager_id ?? null,
    authorizedOtCodeIds: row.authorized_ot_codes ?? undefined,
    rolledOffContractIds: row.rolled_off_contract_ids ?? undefined,
    peStatus: row.pe_status ?? false,
    certifications: coerceCertifications(row.certifications),
    citizenship: coerceCitizenship(row.citizenship),
    gracePeriodHours: row.grace_period_hours,
    wrapRate: row.wrap_rate != null ? Number(row.wrap_rate) : null,
    // TS-MyTS — Streams A/B/G/H (migration 00287)
    payClass: coercePayClass(row.pay_class),
    scheduleType: coerceScheduleType(row.schedule_type),
    weekStartDay: coerceWeekStartDay(row.week_start_day),
    workCategory: coerceWorkCategory(row.work_category),
    wandWorkerId: row.wand_worker_id ?? null,
    customDailyTargets: coerceCustomDailyTargets(row.custom_daily_targets),
    autoFill: row.auto_fill_enabled
      ? {
          enabled: true,
          defaultChargeCodeId: row.auto_fill_default_charge_code_id ?? '',
          mode: (row.auto_fill_mode === 'queue-for-review' ? 'queue-for-review' : 'auto-submit') as 'auto-submit' | 'queue-for-review',
          lastRunAt: row.auto_fill_last_run_at ?? undefined,
        }
      : undefined,
    // Stream F — subcontractor persona linkage
    subVendorId: row.sub_vendor_id ?? null,
    subEmployeeId: row.sub_employee_id ?? null,
    isSubLead: row.is_sub_lead ?? false,
    // Canonical company key (decision #5) — drives persona-cast scoping.
    primaryOrganizationId: row.primary_organization_id ?? null,
  }))
}

/**
 * Patch a single user row with the small whitelist of fields the Settings
 * editor exposes (pay_class / schedule_type / week_start_day). camelCase →
 * snake_case mapping happens here so the service layer stays in app-shape.
 */
export async function updateEmployee(
  employeeId: string,
  patch: { payClass?: string; scheduleType?: string; weekStartDay?: string },
): Promise<void> {
  const dbPatch: Record<string, unknown> = {}
  if (patch.payClass !== undefined) dbPatch.pay_class = patch.payClass
  if (patch.scheduleType !== undefined) dbPatch.schedule_type = patch.scheduleType
  if (patch.weekStartDay !== undefined) dbPatch.week_start_day = patch.weekStartDay
  if (Object.keys(dbPatch).length === 0) return
  const { error } = await supabase.from('users').update(dbPatch).eq('id', employeeId)
  if (error) throw error
}

/**
 * Stream H — fetch approved leaves stored on the user record as a jsonb
 * column. Provisional until pto_requests with status filtering exists.
 */
export async function fetchApprovedLeaves(employeeId: string): Promise<{ startDate: string; endDate: string; ptoChargeCodeId: string; reason?: string }[]> {
  const { data, error } = await supabase
    .from('users')
    .select('approved_leaves')
    .eq('id', employeeId)
    .maybeSingle()
  if (error || !data) return []
  const raw = (data as { approved_leaves?: unknown }).approved_leaves
  if (!Array.isArray(raw)) return []
  return raw
    .filter((x): x is { startDate: string; endDate: string; ptoChargeCodeId: string; reason?: string } =>
      x != null && typeof x === 'object'
      && typeof (x as { startDate?: unknown }).startDate === 'string'
      && typeof (x as { endDate?: unknown }).endDate === 'string'
      && typeof (x as { ptoChargeCodeId?: unknown }).ptoChargeCodeId === 'string',
    )
    .map((l) => ({
      startDate: l.startDate,
      endDate: l.endDate,
      ptoChargeCodeId: l.ptoChargeCodeId,
      reason: typeof l.reason === 'string' ? l.reason : undefined,
    }))
}

// ── Coercion helpers for the new columns ───────────────────────────────────

function coercePayClass(s: string | null): 'salary' | 'hourly' | 'subcontractor' | 'general-time' | undefined {
  if (s === 'salary' || s === 'hourly' || s === 'subcontractor' || s === 'general-time') return s
  return undefined
}

function coerceScheduleType(s: string | null): '5x8' | '4x10' | '9x80' | 'custom' | undefined {
  if (s === '5x8' || s === '4x10' || s === '9x80' || s === 'custom') return s
  return undefined
}

function coerceWeekStartDay(s: string | null): 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | undefined {
  if (s === 'sun' || s === 'mon' || s === 'tue' || s === 'wed' || s === 'thu' || s === 'fri' || s === 'sat') return s
  return undefined
}

function coerceWorkCategory(s: string | null): 'office' | 'manufacturing' | undefined {
  if (s === 'office' || s === 'manufacturing') return s
  return undefined
}

function coerceCustomDailyTargets(raw: unknown): number[] | undefined {
  if (!Array.isArray(raw)) return undefined
  if (raw.length !== 7) return undefined
  if (!raw.every((x) => typeof x === 'number')) return undefined
  return raw as number[]
}

export async function fetchContracts(): Promise<TsContract[]> {
  const { data, error } = await supabase
    .from('master_contracts')
    .select('id, number, title, contractor_name, contract_type, total_value, obligated_amount, start_date, end_date, has_controlled_unclassified_info')
    .order('start_date', { ascending: false, nullsFirst: false })
  if (error) throw error
  return (data as DbMasterContract[]).map((row) => ({
    id: row.id,
    code: row.number ?? row.id,
    name: row.title,
    agency: row.contractor_name ?? 'Unknown',
    type: coerceContractType(row.contract_type),
    fundedValue: Number(row.total_value ?? 0),
    burnedValue: Number(row.obligated_amount ?? 0),
    ceiling: Number(row.total_value ?? 0),
    periodStart: row.start_date,
    periodEnd: row.end_date,
    casCovered: false, // not tracked in master_contracts; future column
    primeOrSub: 'prime',
    cuiMarking: row.has_controlled_unclassified_info ? 'CUI' : undefined,
  }))
}

/**
 * Charge codes — joined through work_authorizations → task_orders → master_contracts.
 * If a work_auth's project_id maps to multiple task_orders, take the first.
 * If no matching task_order exists, contractId is left empty (charge code still
 * usable, just without contract attribution in the UI).
 */
// Base columns (always present). The "Choose Charge Line" axes (00357) are
// appended only when that migration has been applied; selectChargeLines falls
// back to the base set when those columns don't exist yet, so the timesheet
// still loads its charge lines pre-migration instead of failing the whole query.
const CHARGE_LINE_BASE_COLS =
  'id, work_authorization_id, code, name, resource_category_filter, is_active, requires_pe, required_certifications, export_control, cui_marking, clin_id, cost_objective_id, acrn, color_of_money, pop_start, pop_end, work_site, cost_center, gl_account, capital_or_expense, billable, rate_card_id'
const CHARGE_LINE_EXT_COLS = `${CHARGE_LINE_BASE_COLS}, organization_id, discipline_id, time_type_id`

async function selectChargeLines(): Promise<DbChargeLine[]> {
  const ext = await supabase.from('charge_lines').select(CHARGE_LINE_EXT_COLS)
  if (!ext.error) return ext.data as DbChargeLine[]
  // Migration 00357 not applied yet → the org/discipline/time-type columns
  // don't exist. Retry with the base columns so charge lines still load.
  console.warn('[timesheet] charge_lines extended select failed; falling back to base columns', ext.error)
  const base = await supabase.from('charge_lines').select(CHARGE_LINE_BASE_COLS)
  if (base.error) throw base.error
  return base.data as DbChargeLine[]
}

export async function fetchChargeCodes(): Promise<TsChargeCode[]> {
  const [chargeLinesData, workAuthsRes, taskOrdersRes] = await Promise.all([
    selectChargeLines(),
    supabase.from('work_authorizations').select('id, project_id, wbs_element_code, authorized_amount, consumed_amount'),
    supabase.from('task_orders').select('id, master_contract_id, project_id'),
  ])
  if (workAuthsRes.error) throw workAuthsRes.error
  if (taskOrdersRes.error) throw taskOrdersRes.error

  const workAuthsById = new Map<string, DbWorkAuth>()
  for (const wa of workAuthsRes.data as DbWorkAuth[]) workAuthsById.set(wa.id, wa)

  const masterByProject = new Map<string, string>()
  for (const to of taskOrdersRes.data as DbTaskOrder[]) {
    if (to.project_id && to.master_contract_id && !masterByProject.has(to.project_id)) {
      masterByProject.set(to.project_id, to.master_contract_id)
    }
  }

  return chargeLinesData.map((row) => {
    const wa = workAuthsById.get(row.work_authorization_id)
    const contractId = wa ? (masterByProject.get(wa.project_id) ?? '') : ''
    const wbs = wa?.wbs_element_code ?? row.code
    const labors = row.resource_category_filter ? [row.resource_category_filter] : []
    const exportCtrl = row.export_control === 'ITAR' || row.export_control === 'EAR' ? row.export_control : null
    const capOrExp = row.capital_or_expense === 'capital' || row.capital_or_expense === 'expense'
      ? row.capital_or_expense
      : null
    return {
      id: row.id,
      contractId,
      wbs,
      description: row.name,
      laborCategories: labors,
      allowable: row.is_active,
      requiresPE: row.requires_pe ?? false,
      requiredCertifications: row.required_certifications ?? undefined,
      exportControl: exportCtrl,
      cuiMarking: row.cui_marking,
      // Composable dimensions (#8). Inherited axes (wbs / pool / billable) are
      // resolved in lib/chargeCodeDimensions.ts against the linked refs.
      costObjectiveId: row.cost_objective_id,
      acrn: row.acrn,
      colorOfMoney: row.color_of_money,
      popStart: row.pop_start,
      popEnd: row.pop_end,
      workSite: row.work_site,
      costCenter: row.cost_center,
      glAccount: row.gl_account,
      capitalOrExpense: capOrExp,
      billable: row.billable,
      rateCardId: row.rate_card_id,
      // "Choose Charge Line" axes (Phase 4) — soft refs resolved in the app.
      organizationId: row.organization_id ?? null,
      disciplineId: row.discipline_id ?? null,
      timeTypeId: row.time_type_id ?? null,
    }
  })
}

export async function submitWeek(employeeId: string, weekStart: string, orgId?: string): Promise<TsSubmission> {
  // Compute totals from the persisted entries for the week.
  const entries = await getEntriesForWeek(employeeId, weekStart)
  const totalHours = entries.reduce((sum, e) => sum + e.hours, 0)
  const now = new Date().toISOString()

  const { data, error } = await supabase
    .from('timesheet_submissions')
    .upsert(
      {
        employee_id: employeeId,
        week_start: weekStart,
        status: 'submitted',
        submitted_at: now,
        total_hours: totalHours,
      },
      { onConflict: 'employee_id,week_start' },
    )
    .select('id, employee_id, week_start, status, submitted_at, approved_at, rejection_reason, total_hours, workflow_instance_id')
    .single()
  if (error) throw error

  const submission = rowToSubmission(data as DbSubmission)
  if (!submission) throw new Error('Submitted submission unexpectedly mapped to null')

  // Side-channel: spawn a workflow_instance and write the back-reference.
  // Resolve the approver from the LIVE roster (users.manager_id) so per-company
  // cast members route to their real manager, not just the mock anchors.
  const approverRoster = await fetchEmployees()
  const supervisorId = approverRoster.find((e) => e.id === employeeId || e.pernr === employeeId)?.supervisorId ?? null
  const wfInstanceId = await spawnApprovalWorkflow(submission.id, employeeId, supervisorId, weekStart)
  if (wfInstanceId) {
    const { error: linkErr } = await supabase
      .from('timesheet_submissions')
      .update({ workflow_instance_id: wfInstanceId })
      .eq('id', submission.id)
    if (linkErr) console.warn('[timesheet] workflow_instance_id back-link failed', linkErr)
  }

  // Charge-object approval mode: route each line to its approver and create one
  // approval segment per approver. Guarded by the org methodology — manager mode
  // skips this entirely (zero change). Never fails the user submit.
  if (getStoredApprovalMethodology() === 'charge_object') {
    try {
      await createApprovalSegments(resolveOrgId(orgId), submission.id, employeeId, entries)
    } catch (e) {
      console.warn('[timesheet] createApprovalSegments failed (submission still saved)', e)
    }
  }

  // SAP CATS bridge — post to status 20 (mock provider; production lands when
  // a BAPI/OData backend is wired). Side-channel, never fails the user submit.
  await postToCats(submission, employeeId)

  return submission
}

// ─── Charge-object approval segments (Time Approval Routing, Part 1) ──────────
// In charge-object mode a single weekly submission fans out to one approval
// segment per routed approver. Manager mode never touches any of this.

function resolveOrgId(orgId?: string): string {
  return orgId ?? getCurrentOrganizationId() ?? 'org_demo'
}

async function getWorkflowInstanceId(submissionId: string): Promise<string | null> {
  const { data } = await supabase
    .from('timesheet_submissions').select('workflow_instance_id').eq('id', submissionId).maybeSingle()
  return (data as { workflow_instance_id: string | null } | null)?.workflow_instance_id ?? null
}

/** Build the routing context: charge-object approvers + the worker's manager. */
async function buildRoutingContext(orgId: string, employeeId: string): Promise<{ approverByChargeCode: Map<string, string>; managerUserId: string | null }> {
  const [approvers, roster] = await Promise.all([listChargeObjectApprovers(orgId), fetchEmployees()])
  const approverByChargeCode = new Map<string, string>()
  // charge_object_approvers.id === the SAP charge object synthetic id === the
  // entry's charge_code_id, so the routing engine keys directly off it.
  for (const a of approvers) approverByChargeCode.set(a.id, a.approverUserId)
  const worker = roster.find((e) => e.id === employeeId || e.pernr === employeeId)
  return { approverByChargeCode, managerUserId: worker?.supervisorId ?? null }
}

/**
 * Route a submission's lines and (re)create its approval segments. Stamps each
 * line's routed_approver_id. Idempotent on re-submit (stale routing + segments
 * are cleared first). Returns segment + unrouted counts.
 */
export async function createApprovalSegments(
  orgId: string, submissionId: string, employeeId: string, entries: ReadonlyArray<TsTimeEntry>,
): Promise<{ segments: number; unrouted: number }> {
  const ctx = await buildRoutingContext(orgId, employeeId)
  const routingEntries: RoutingEntry[] = entries.map((e) => ({ id: e.id, chargeCodeId: e.chargeCodeId, hours: e.hours }))
  const { segments, unrouted } = routeSubmission(routingEntries, ctx)

  // Clear prior routing + segments for a clean re-submit.
  const entryIds = entries.map((e) => e.id)
  if (entryIds.length > 0) {
    const { error } = await supabase.from('timesheet_time_entries').update({ routed_approver_id: null }).in('id', entryIds)
    if (error) console.warn('[timesheet] clear routed_approver_id failed', error)
  }
  await supabase.from('timesheet_approval_segments').delete().eq('org_id', orgId).eq('submission_id', submissionId)

  if (segments.length === 0) return { segments: 0, unrouted: unrouted.length }

  // Stamp each line's routed approver, then write the segment rows.
  for (const s of segments) {
    const { error } = await supabase
      .from('timesheet_time_entries').update({ routed_approver_id: s.approverUserId }).in('id', s.entryIds)
    if (error) console.warn('[timesheet] routed_approver_id stamp failed', error)
  }
  const now = new Date().toISOString()
  const rows = segments.map((s) => ({
    org_id: orgId,
    id: `${submissionId}:${s.approverUserId}`,
    submission_id: submissionId,
    approver_user_id: s.approverUserId,
    status: 'pending',
    total_hours: s.totalHours,
    created_at: now,
    updated_at: now,
  }))
  const { error: segErr } = await supabase.from('timesheet_approval_segments').upsert(rows, { onConflict: 'org_id,id' })
  if (segErr) console.warn('[timesheet] approval segment insert failed', segErr)
  return { segments: segments.length, unrouted: unrouted.length }
}

export interface PendingApprovalSegment {
  segmentId: string
  submissionId: string
  approverUserId: string
  employeeId: string
  weekStart: string
  submittedAt: string
  segmentHours: number
}

/**
 * Segments awaiting THIS approver, in charge-object mode. Resolves the approver
 * to their canonical user id(s) so a persona / pernr both match. Skips segments
 * whose submission already resolved (e.g. rejected by another segment).
 */
export async function getPendingApprovalSegments(approverId: string, orgId?: string): Promise<PendingApprovalSegment[]> {
  const org = resolveOrgId(orgId)
  const roster = await fetchEmployees()
  const me = roster.find((e) => e.id === approverId || e.pernr === approverId)
  // My own ids + the ids of anyone whose authority is actively delegated to me,
  // so a delegate sees the segments routed to the delegator (D3).
  const delegators = await activeDelegatorIdsForApprover(me?.id ?? approverId)
  const ids = [...new Set([approverId, me?.id, me?.pernr, ...delegators].filter((x): x is string => !!x))]

  const { data: segRows, error } = await supabase
    .from('timesheet_approval_segments')
    .select('id, submission_id, approver_user_id, total_hours')
    .eq('org_id', org)
    .in('approver_user_id', ids)
    .eq('status', 'pending')
  if (error) { console.warn('[timesheet] getPendingApprovalSegments', error); return [] }
  const segs = (segRows ?? []) as Array<{ id: string; submission_id: string; approver_user_id: string; total_hours: number | null }>
  if (segs.length === 0) return []

  const subIds = [...new Set(segs.map((s) => s.submission_id))]
  const { data: subRows } = await supabase
    .from('timesheet_submissions')
    .select('id, employee_id, week_start, submitted_at, status')
    .in('id', subIds)
  const subById = new Map<string, { employee_id: string; week_start: string; submitted_at: string | null; status: string }>()
  for (const r of (subRows ?? []) as Array<{ id: string; employee_id: string; week_start: string; submitted_at: string | null; status: string }>) {
    subById.set(r.id, r)
  }

  const out: PendingApprovalSegment[] = []
  for (const s of segs) {
    const sub = subById.get(s.submission_id)
    if (!sub || sub.status !== 'submitted') continue
    out.push({
      segmentId: s.id, submissionId: s.submission_id, approverUserId: s.approver_user_id,
      employeeId: sub.employee_id, weekStart: sub.week_start,
      submittedAt: sub.submitted_at ?? new Date().toISOString(),
      segmentHours: Number(s.total_hours ?? 0),
    })
  }
  return out.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt))
}

/**
 * Decide one approval segment, then recompute + persist the derived submission
 * status (all approved -> approved; any rejected -> rejected; else pending).
 * Submission-level effects (workflow + CATS) fire once on the terminal
 * transition. Each segment decision is audited against the workflow instance.
 */
export async function decideApprovalSegment(
  segmentId: string,
  decision: 'approved' | 'rejected',
  actorId: string | null = null,
  reasonCode: string | null = null,
  orgId?: string,
): Promise<void> {
  const org = resolveOrgId(orgId)
  const { data: segRow, error: segErr } = await supabase
    .from('timesheet_approval_segments').select('submission_id').eq('org_id', org).eq('id', segmentId).maybeSingle()
  if (segErr) throw segErr
  const submissionId = (segRow as { submission_id: string } | null)?.submission_id
  if (!submissionId) throw new Error('Approval segment not found')

  const { error: updErr } = await supabase
    .from('timesheet_approval_segments')
    .update({
      status: decision,
      decided_by: actorId,
      decided_at: new Date().toISOString(),
      rejection_reason_code: decision === 'rejected' ? reasonCode : null,
      updated_at: new Date().toISOString(),
    })
    .eq('org_id', org).eq('id', segmentId)
  if (updErr) throw updErr

  // Recompute the submission status from ALL its segments.
  const { data: allSegs } = await supabase
    .from('timesheet_approval_segments').select('status').eq('org_id', org).eq('submission_id', submissionId)
  const statuses = ((allSegs ?? []) as Array<{ status: SegmentStatus }>).map((r) => r.status)
  const derived = deriveSubmissionStatus(statuses)

  const { data: subRow } = await supabase
    .from('timesheet_submissions').select('status').eq('id', submissionId).maybeSingle()
  const current = (subRow as { status: string } | null)?.status
  if (derived === 'approved' && current !== 'approved') {
    await applyApprovedEffects(submissionId, actorId)
  } else if (derived === 'rejected' && current !== 'rejected') {
    await applyRejectedEffects(submissionId, actorId, reasonCode)
  }
  // derived 'pending' -> leave the submission 'submitted' (other segments open).

  const wfId = await getWorkflowInstanceId(submissionId)
  if (wfId) await writeAuditLog(wfId, `segment_${decision}`, actorId, { submission_id: submissionId, segment_id: segmentId })
}
